import { useState, useMemo, useRef, useEffect } from 'react'
import { useQuery, useMutation } from '@tanstack/react-query'
import {
  Volume2,
  Search,
  Play,
  Square,
  ArrowUpDown,
  Star,
  StarOff,
  X,
  RefreshCw,
  Mic,
  User,
  Tags,
  Grid3X3,
  List,
  Globe,
  Copy,
  Check,
} from 'lucide-react'
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Button,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Badge,
  Label,
  Textarea,
} from '@/components/ui'
import { voicesApi, type Voice } from '@/api/endpoints/voices'
import { cn } from '@/lib/utils'

// Default sample texts for voice preview
const SAMPLE_TEXTS = [
  "Hello! Thank you for calling Riviera Finance. How can I help you today?",
  "I'd be happy to explain our freight factoring services. We help trucking companies get paid within 24 hours instead of waiting 30 to 60 days.",
  "Let me connect you with one of our specialists who can provide more detailed information about your specific situation.",
]

// Voice tags to extract for filtering
const extractTags = (voices: Voice[]): string[] => {
  const tagSet = new Set<string>()
  voices.forEach(voice => {
    voice.tags?.forEach(tag => {
      if (tag) tagSet.add(tag.toLowerCase())
    })
  })
  return Array.from(tagSet).sort()
}

// Gender options for filtering
const GENDER_OPTIONS = ['all', 'male', 'female', 'neutral'] as const
type GenderFilter = typeof GENDER_OPTIONS[number]

// Language options for filtering
const LANGUAGE_OPTIONS = ['all', 'english', 'spanish', 'french', 'german', 'portuguese', 'italian', 'chinese', 'japanese', 'korean', 'arabic', 'hindi'] as const
type LanguageFilter = typeof LANGUAGE_OPTIONS[number]

// View mode
type ViewMode = 'grid' | 'table'

// Sort options
const SORT_OPTIONS = [
  { value: 'name-asc', label: 'Name (A-Z)' },
  { value: 'name-desc', label: 'Name (Z-A)' },
  { value: 'rating-desc', label: 'Rating (High to Low)' },
  { value: 'rating-asc', label: 'Rating (Low to High)' },
] as const
type SortOption = typeof SORT_OPTIONS[number]['value']

export function Voices() {
  // State
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedTag, setSelectedTag] = useState<string>('all')
  const [selectedGender, setSelectedGender] = useState<GenderFilter>('all')
  const [selectedLanguage, setSelectedLanguage] = useState<LanguageFilter>('all')
  const [sortBy, setSortBy] = useState<SortOption>('name-asc')
  const [previewText, setPreviewText] = useState(SAMPLE_TEXTS[0])
  const [playingVoiceId, setPlayingVoiceId] = useState<string | null>(null)
  const [selectedVoice, setSelectedVoice] = useState<Voice | null>(null)
  const [viewMode, setViewMode] = useState<ViewMode>('grid')
  const [copiedId, setCopiedId] = useState<string | null>(null)
  const audioRef = useRef<HTMLAudioElement | null>(null)

  // Fetch all voices
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['voices'],
    queryFn: () => voicesApi.getAll(),
  })

  const voices = data?.data?.voices || []
  const defaultVoiceId = data?.data?.default_voice_id

  // Extract available tags
  const availableTags = useMemo(() => extractTags(voices), [voices])

  // Voice preview mutation
  const previewMutation = useMutation({
    mutationFn: ({ voiceId, text }: { voiceId: string; text: string }) =>
      voicesApi.preview(voiceId, text),
    onSuccess: (response, variables) => {
      const audioData = response.data.audio_data
      const contentType = response.data.content_type || 'audio/mpeg'
      if (audioData && audioRef.current) {
        // Create data URL from base64 audio
        audioRef.current.src = `data:${contentType};base64,${audioData}`
        audioRef.current.play()
        setPlayingVoiceId(variables.voiceId)
      }
    },
    onError: (error) => {
      console.error('Voice preview failed:', error)
      setPlayingVoiceId(null)
    },
  })

  // Cleanup audio on unmount
  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause()
        audioRef.current = null
      }
    }
  }, [])

  // Handle audio ended
  const handleAudioEnded = () => {
    setPlayingVoiceId(null)
  }

  // Stop playing
  const stopPlaying = () => {
    if (audioRef.current) {
      audioRef.current.pause()
      audioRef.current.currentTime = 0
    }
    setPlayingVoiceId(null)
  }

  // Play voice preview
  const playPreview = (voice: Voice) => {
    if (playingVoiceId === voice.id) {
      stopPlaying()
      return
    }
    stopPlaying()
    previewMutation.mutate({ voiceId: voice.id, text: previewText })
  }

  // Filter and sort voices
  const filteredVoices = useMemo(() => {
    let result = [...voices]

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase()
      result = result.filter(v =>
        v.name?.toLowerCase().includes(query) ||
        v.description?.toLowerCase().includes(query) ||
        v.tags?.some(t => t?.toLowerCase().includes(query))
      )
    }

    // Tag filter
    if (selectedTag !== 'all') {
      result = result.filter(v =>
        v.tags?.some(t => t?.toLowerCase() === selectedTag)
      )
    }

    // Gender filter (infer from name/description/tags)
    if (selectedGender !== 'all') {
      result = result.filter(v => {
        const name = v.name?.toLowerCase() || ''
        const desc = v.description?.toLowerCase() || ''
        const tags = v.tags?.map(t => t?.toLowerCase()) || []
        const allText = `${name} ${desc} ${tags.join(' ')}`

        if (selectedGender === 'male') {
          return allText.includes('male') && !allText.includes('female')
        } else if (selectedGender === 'female') {
          return allText.includes('female')
        } else if (selectedGender === 'neutral') {
          return !allText.includes('male') && !allText.includes('female')
        }
        return true
      })
    }

    // Language filter (infer from name/description/tags)
    if (selectedLanguage !== 'all') {
      result = result.filter(v => {
        const name = v.name?.toLowerCase() || ''
        const desc = v.description?.toLowerCase() || ''
        const tags = v.tags?.map(t => t?.toLowerCase()) || []
        const allText = `${name} ${desc} ${tags.join(' ')}`
        return allText.includes(selectedLanguage)
      })
    }

    // Sort
    result.sort((a, b) => {
      switch (sortBy) {
        case 'name-asc':
          return (a.name || '').localeCompare(b.name || '')
        case 'name-desc':
          return (b.name || '').localeCompare(a.name || '')
        case 'rating-desc':
          return (b.average_rating || 0) - (a.average_rating || 0)
        case 'rating-asc':
          return (a.average_rating || 0) - (b.average_rating || 0)
        default:
          return 0
      }
    })

    return result
  }, [voices, searchQuery, selectedTag, selectedGender, selectedLanguage, sortBy])

  // Render rating stars
  const renderRating = (rating?: number) => {
    if (!rating) return null
    const stars = Math.round(rating)
    return (
      <div className="flex items-center gap-0.5">
        {[1, 2, 3, 4, 5].map(i => (
          i <= stars ? (
            <Star key={i} className="h-3 w-3 fill-yellow-400 text-yellow-400" />
          ) : (
            <StarOff key={i} className="h-3 w-3 text-gray-300" />
          )
        ))}
      </div>
    )
  }

  // Infer gender from voice metadata
  const inferGender = (voice: Voice): string | null => {
    const text = `${voice.name || ''} ${voice.description || ''} ${voice.tags?.join(' ') || ''}`.toLowerCase()
    if (text.includes('female')) return 'Female'
    if (text.includes('male')) return 'Male'
    return null
  }

  // Infer language from voice metadata
  const inferLanguage = (voice: Voice): string | null => {
    const text = `${voice.name || ''} ${voice.description || ''} ${voice.tags?.join(' ') || ''}`.toLowerCase()
    const languages = [
      { key: 'english', label: 'English' },
      { key: 'spanish', label: 'Spanish' },
      { key: 'french', label: 'French' },
      { key: 'german', label: 'German' },
      { key: 'portuguese', label: 'Portuguese' },
      { key: 'italian', label: 'Italian' },
      { key: 'chinese', label: 'Chinese' },
      { key: 'japanese', label: 'Japanese' },
      { key: 'korean', label: 'Korean' },
      { key: 'arabic', label: 'Arabic' },
      { key: 'hindi', label: 'Hindi' },
      { key: 'british', label: 'English (British)' },
      { key: 'american', label: 'English (American)' },
      { key: 'australian', label: 'English (Australian)' },
    ]
    for (const lang of languages) {
      if (text.includes(lang.key)) return lang.label
    }
    return null
  }

  // Copy voice ID to clipboard
  const copyVoiceId = (voiceId: string) => {
    navigator.clipboard.writeText(voiceId)
    setCopiedId(voiceId)
    setTimeout(() => setCopiedId(null), 2000)
  }

  if (error) {
    return (
      <div className="p-6">
        <Card className="border-destructive">
          <CardContent className="pt-6">
            <p className="text-destructive">Failed to load voices. Please try again.</p>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="p-6 space-y-6">
      {/* Hidden audio element for playback */}
      <audio
        ref={audioRef}
        onEnded={handleAudioEnded}
        style={{ display: 'none' }}
      />

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Voice Library</h1>
          <p className="text-muted-foreground">
            Browse and preview available AI voices for calls
          </p>
        </div>
        <div className="flex items-center gap-2">
          {/* View Toggle */}
          <div className="flex items-center border rounded-lg">
            <Button
              variant={viewMode === 'grid' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('grid')}
              className="rounded-r-none"
            >
              <Grid3X3 className="h-4 w-4" />
            </Button>
            <Button
              variant={viewMode === 'table' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('table')}
              className="rounded-l-none"
            >
              <List className="h-4 w-4" />
            </Button>
          </div>
          <Button variant="outline" onClick={() => refetch()}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Volume2 className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{voices.length}</p>
                <p className="text-sm text-muted-foreground">Total Voices</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-pink-100 rounded-lg">
                <User className="h-5 w-5 text-pink-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">
                  {voices.filter(v => inferGender(v) === 'Female').length}
                </p>
                <p className="text-sm text-muted-foreground">Female Voices</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-indigo-100 rounded-lg">
                <User className="h-5 w-5 text-indigo-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">
                  {voices.filter(v => inferGender(v) === 'Male').length}
                </p>
                <p className="text-sm text-muted-foreground">Male Voices</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <Tags className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{availableTags.length}</p>
                <p className="text-sm text-muted-foreground">Categories</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Preview Text Section */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <Mic className="h-5 w-5" />
            Preview Text
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <Textarea
              value={previewText}
              onChange={(e) => setPreviewText(e.target.value)}
              placeholder="Enter text to preview with voices..."
              rows={2}
              maxLength={500}
            />
            <div className="flex gap-2 flex-wrap">
              {SAMPLE_TEXTS.map((text, i) => (
                <Button
                  key={i}
                  variant={previewText === text ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setPreviewText(text)}
                >
                  Sample {i + 1}
                </Button>
              ))}
            </div>
            <p className="text-xs text-muted-foreground">
              {previewText.length}/500 characters
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-wrap gap-4 items-end">
            {/* Search */}
            <div className="flex-1 min-w-[200px]">
              <Label className="text-xs text-muted-foreground mb-1.5 block">Search</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search voices..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9"
                />
              </div>
            </div>

            {/* Tag Filter */}
            <div className="w-[180px]">
              <Label className="text-xs text-muted-foreground mb-1.5 block">Category</Label>
              <Select value={selectedTag} onValueChange={setSelectedTag}>
                <SelectTrigger>
                  <SelectValue placeholder="All Categories" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {availableTags.map(tag => (
                    <SelectItem key={tag} value={tag}>
                      {tag.charAt(0).toUpperCase() + tag.slice(1)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Gender Filter */}
            <div className="w-[140px]">
              <Label className="text-xs text-muted-foreground mb-1.5 block">Gender</Label>
              <Select value={selectedGender} onValueChange={(v) => setSelectedGender(v as GenderFilter)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="male">Male</SelectItem>
                  <SelectItem value="female">Female</SelectItem>
                  <SelectItem value="neutral">Neutral</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Language Filter */}
            <div className="w-[160px]">
              <Label className="text-xs text-muted-foreground mb-1.5 block">Language</Label>
              <Select value={selectedLanguage} onValueChange={(v) => setSelectedLanguage(v as LanguageFilter)}>
                <SelectTrigger>
                  <Globe className="h-4 w-4 mr-2" />
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Languages</SelectItem>
                  <SelectItem value="english">English</SelectItem>
                  <SelectItem value="spanish">Spanish</SelectItem>
                  <SelectItem value="french">French</SelectItem>
                  <SelectItem value="german">German</SelectItem>
                  <SelectItem value="portuguese">Portuguese</SelectItem>
                  <SelectItem value="italian">Italian</SelectItem>
                  <SelectItem value="chinese">Chinese</SelectItem>
                  <SelectItem value="japanese">Japanese</SelectItem>
                  <SelectItem value="korean">Korean</SelectItem>
                  <SelectItem value="arabic">Arabic</SelectItem>
                  <SelectItem value="hindi">Hindi</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Sort */}
            <div className="w-[180px]">
              <Label className="text-xs text-muted-foreground mb-1.5 block">Sort By</Label>
              <Select value={sortBy} onValueChange={(v) => setSortBy(v as SortOption)}>
                <SelectTrigger>
                  <ArrowUpDown className="h-4 w-4 mr-2" />
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SORT_OPTIONS.map(opt => (
                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Reset Filters */}
            {(searchQuery || selectedTag !== 'all' || selectedGender !== 'all' || selectedLanguage !== 'all') && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setSearchQuery('')
                  setSelectedTag('all')
                  setSelectedGender('all')
                  setSelectedLanguage('all')
                }}
              >
                <X className="h-4 w-4 mr-1" />
                Clear Filters
              </Button>
            )}
          </div>

          <p className="text-sm text-muted-foreground mt-4">
            Showing {filteredVoices.length} of {voices.length} voices
          </p>
        </CardContent>
      </Card>

      {/* Voices Display */}
      {isLoading ? (
        viewMode === 'grid' ? (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {[1, 2, 3, 4, 5, 6, 7, 8].map(i => (
              <Card key={i} className="animate-pulse">
                <CardContent className="pt-6">
                  <div className="h-32 bg-muted rounded" />
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <Card className="animate-pulse">
            <CardContent className="pt-6">
              <div className="h-64 bg-muted rounded" />
            </CardContent>
          </Card>
        )
      ) : filteredVoices.length === 0 ? (
        <Card>
          <CardContent className="py-12">
            <div className="text-center">
              <Volume2 className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="font-medium text-lg">No voices found</h3>
              <p className="text-muted-foreground">
                Try adjusting your filters or search query
              </p>
            </div>
          </CardContent>
        </Card>
      ) : viewMode === 'table' ? (
        /* Table View */
        <Card>
          <CardContent className="pt-6">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-3 px-4 font-medium text-sm">Name</th>
                    <th className="text-left py-3 px-4 font-medium text-sm">Description</th>
                    <th className="text-left py-3 px-4 font-medium text-sm">Gender</th>
                    <th className="text-left py-3 px-4 font-medium text-sm">Language</th>
                    <th className="text-left py-3 px-4 font-medium text-sm">Rating</th>
                    <th className="text-left py-3 px-4 font-medium text-sm">Tags</th>
                    <th className="text-center py-3 px-4 font-medium text-sm">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredVoices.map(voice => {
                    const isPlaying = playingVoiceId === voice.id
                    const isDefault = voice.id === defaultVoiceId
                    const gender = inferGender(voice)
                    const language = inferLanguage(voice)

                    return (
                      <tr
                        key={voice.id}
                        className={cn(
                          'border-b hover:bg-muted/50 cursor-pointer transition-colors',
                          isPlaying && 'bg-primary/5',
                          selectedVoice?.id === voice.id && 'bg-blue-50'
                        )}
                        onClick={() => setSelectedVoice(voice)}
                      >
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-2">
                            <div className={cn(
                              'p-1.5 rounded-lg',
                              gender === 'Female' ? 'bg-pink-100' :
                              gender === 'Male' ? 'bg-indigo-100' : 'bg-gray-100'
                            )}>
                              <Mic className={cn(
                                'h-3 w-3',
                                gender === 'Female' ? 'text-pink-600' :
                                gender === 'Male' ? 'text-indigo-600' : 'text-gray-600'
                              )} />
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-sm">{voice.name}</span>
                              {isDefault && (
                                <Star className="h-3 w-3 fill-yellow-400 text-yellow-400" />
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <span className="text-sm text-muted-foreground line-clamp-1 max-w-[200px]">
                            {voice.description || '-'}
                          </span>
                        </td>
                        <td className="py-3 px-4">
                          {gender ? (
                            <Badge variant="outline" className="text-xs">
                              {gender}
                            </Badge>
                          ) : '-'}
                        </td>
                        <td className="py-3 px-4">
                          {language ? (
                            <Badge variant="secondary" className="text-xs">
                              <Globe className="h-3 w-3 mr-1" />
                              {language}
                            </Badge>
                          ) : '-'}
                        </td>
                        <td className="py-3 px-4">
                          {voice.average_rating ? renderRating(voice.average_rating) : '-'}
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex flex-wrap gap-1 max-w-[150px]">
                            {voice.tags?.slice(0, 2).map((tag, i) => (
                              <Badge key={i} variant="secondary" className="text-xs py-0">
                                {tag}
                              </Badge>
                            ))}
                            {voice.tags && voice.tags.length > 2 && (
                              <Badge variant="outline" className="text-xs py-0">
                                +{voice.tags.length - 2}
                              </Badge>
                            )}
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex items-center justify-center gap-1">
                            <Button
                              size="icon"
                              variant={isPlaying ? 'default' : 'outline'}
                              className="h-7 w-7"
                              onClick={(e) => {
                                e.stopPropagation()
                                playPreview(voice)
                              }}
                              disabled={previewMutation.isPending && !isPlaying}
                            >
                              {isPlaying ? (
                                <Square className="h-3 w-3" />
                              ) : (
                                <Play className="h-3 w-3" />
                              )}
                            </Button>
                            <Button
                              size="icon"
                              variant="ghost"
                              className="h-7 w-7"
                              onClick={(e) => {
                                e.stopPropagation()
                                copyVoiceId(voice.id)
                              }}
                            >
                              {copiedId === voice.id ? (
                                <Check className="h-3 w-3 text-green-600" />
                              ) : (
                                <Copy className="h-3 w-3" />
                              )}
                            </Button>
                          </div>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      ) : (
        /* Grid View */
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {filteredVoices.map(voice => {
            const isPlaying = playingVoiceId === voice.id
            const isDefault = voice.id === defaultVoiceId
            const gender = inferGender(voice)

            return (
              <Card
                key={voice.id}
                className={cn(
                  'relative transition-all hover:shadow-md cursor-pointer',
                  isPlaying && 'ring-2 ring-primary',
                  isDefault && 'ring-2 ring-yellow-400',
                  selectedVoice?.id === voice.id && 'ring-2 ring-blue-500'
                )}
                onClick={() => setSelectedVoice(voice)}
              >
                <CardContent className="pt-6">
                  {/* Header */}
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <div className={cn(
                        'p-2 rounded-lg',
                        gender === 'Female' ? 'bg-pink-100' :
                        gender === 'Male' ? 'bg-indigo-100' : 'bg-gray-100'
                      )}>
                        <Mic className={cn(
                          'h-4 w-4',
                          gender === 'Female' ? 'text-pink-600' :
                          gender === 'Male' ? 'text-indigo-600' : 'text-gray-600'
                        )} />
                      </div>
                      <div>
                        <h3 className="font-semibold text-sm line-clamp-1">{voice.name}</h3>
                        {gender && (
                          <p className="text-xs text-muted-foreground">{gender}</p>
                        )}
                      </div>
                    </div>

                    {/* Play Button */}
                    <Button
                      size="icon"
                      variant={isPlaying ? 'default' : 'outline'}
                      className="h-8 w-8"
                      onClick={(e) => {
                        e.stopPropagation()
                        playPreview(voice)
                      }}
                      disabled={previewMutation.isPending && !isPlaying}
                    >
                      {isPlaying ? (
                        <Square className="h-4 w-4" />
                      ) : previewMutation.isPending ? (
                        <RefreshCw className="h-4 w-4 animate-spin" />
                      ) : (
                        <Play className="h-4 w-4" />
                      )}
                    </Button>
                  </div>

                  {/* Description */}
                  {voice.description && (
                    <p className="text-xs text-muted-foreground mb-3 line-clamp-2">
                      {voice.description}
                    </p>
                  )}

                  {/* Rating */}
                  {voice.average_rating && (
                    <div className="mb-3">
                      {renderRating(voice.average_rating)}
                    </div>
                  )}

                  {/* Tags */}
                  {voice.tags && voice.tags.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {voice.tags.slice(0, 4).map((tag, i) => (
                        <Badge key={i} variant="secondary" className="text-xs py-0">
                          {tag}
                        </Badge>
                      ))}
                      {voice.tags.length > 4 && (
                        <Badge variant="outline" className="text-xs py-0">
                          +{voice.tags.length - 4}
                        </Badge>
                      )}
                    </div>
                  )}

                  {/* Badges */}
                  <div className="flex gap-2 mt-3">
                    {isDefault && (
                      <Badge className="bg-yellow-100 text-yellow-800 text-xs">
                        <Star className="h-3 w-3 mr-1 fill-current" />
                        Default
                      </Badge>
                    )}
                    {isPlaying && (
                      <Badge className="bg-green-100 text-green-800 text-xs animate-pulse">
                        Playing...
                      </Badge>
                    )}
                  </div>
                </CardContent>
              </Card>
            )
          })}
        </div>
      )}

      {/* Selected Voice Details Panel */}
      {selectedVoice && (
        <Card className="fixed bottom-4 right-4 w-96 shadow-xl z-50">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg">Voice Details</CardTitle>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setSelectedVoice(null)}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div>
                <h3 className="font-semibold text-lg">{selectedVoice.name}</h3>
                {selectedVoice.description && (
                  <p className="text-sm text-muted-foreground mt-1">{selectedVoice.description}</p>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-muted-foreground">Voice ID</p>
                  <p className="font-mono text-xs">{selectedVoice.id.slice(0, 16)}...</p>
                </div>
                {inferGender(selectedVoice) && (
                  <div>
                    <p className="text-muted-foreground">Gender</p>
                    <p>{inferGender(selectedVoice)}</p>
                  </div>
                )}
                {selectedVoice.average_rating && (
                  <div>
                    <p className="text-muted-foreground">Rating</p>
                    {renderRating(selectedVoice.average_rating)}
                  </div>
                )}
                {selectedVoice.ratings && (
                  <div>
                    <p className="text-muted-foreground">Reviews</p>
                    <p>{selectedVoice.ratings}</p>
                  </div>
                )}
              </div>

              {selectedVoice.tags && selectedVoice.tags.length > 0 && (
                <div>
                  <p className="text-muted-foreground text-sm mb-2">Tags</p>
                  <div className="flex flex-wrap gap-1">
                    {selectedVoice.tags.map((tag, i) => (
                      <Badge key={i} variant="secondary">{tag}</Badge>
                    ))}
                  </div>
                </div>
              )}

              <Button
                className="w-full"
                onClick={() => playPreview(selectedVoice)}
                disabled={previewMutation.isPending}
              >
                {playingVoiceId === selectedVoice.id ? (
                  <>
                    <Square className="h-4 w-4 mr-2" />
                    Stop Preview
                  </>
                ) : previewMutation.isPending ? (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    Loading...
                  </>
                ) : (
                  <>
                    <Play className="h-4 w-4 mr-2" />
                    Play Preview
                  </>
                )}
              </Button>

              <Button
                variant="outline"
                className="w-full"
                onClick={() => {
                  navigator.clipboard.writeText(selectedVoice.id)
                }}
              >
                Copy Voice ID
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  )
}

export default Voices
